'use client';

import { LeftArrowIcon } from '@insta-club/components';
import { Calendar } from '@insta-club/models';
import { cn, formatDate, isSameJalaaliDate } from '@insta-club/utilities';
import { useCallback, useMemo } from 'react';

interface Props {
    value?: Calendar;
    calendar?: Calendar[];
    onChange?: (day: Calendar) => void;
}

const ProgramCalendarHead: React.FC<Props> = ({ value, calendar, onChange }) => {
    const dateLabel = useMemo(
        () => (value ? (isSameJalaaliDate(value.date) ? 'امروز' : formatDate(value.date, '%d %m')) : ''),
        [value]
    );

    const currentIndex = useMemo(() => {
        if (!calendar || !value) return -1;
        return calendar.findIndex((d) => d.day === value.day);
    }, [calendar, value]);

    const hasPrevDay = currentIndex > 0;
    const hasNextDay = currentIndex >= 0 && currentIndex < (calendar?.length ?? 0) - 1;

    const handleNavigate = useCallback(
        (direction: 'prev' | 'next') => () => {
            if (!calendar || currentIndex === -1) return;
            const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
            const newDay = calendar[newIndex];
            if (newDay) {
                onChange?.(newDay);
            }
        },
        [calendar, currentIndex, onChange]
    );

    if (!calendar) return;

    return (
        <div className='flex items-center py-1'>
            <ArrowButton
                title='روز قبل'
                icon={<LeftArrowIcon width={6} height={10} />}
                rotate='rotate-180'
                onClick={handleNavigate('prev')}
                disabled={!hasPrevDay}
            />
            <div className='flex flex-1 flex-col items-center justify-center text-center'>
                <span className='text-base font-bold text-gray-800'>{dateLabel}</span>
            </div>
            <ArrowButton
                title='روز بعد'
                icon={<LeftArrowIcon width={6} height={10} />}
                className='flex-row-reverse'
                onClick={handleNavigate('next')}
                disabled={!hasNextDay}
            />
        </div>
    );
};

interface ArrowButtonProps {
    title: string;
    rotate?: string;
    className?: string;
    disabled?: boolean;
    onClick: () => void;
    icon: React.ReactElement;
}

const ArrowButton: React.FC<ArrowButtonProps> = ({ icon, className, onClick, rotate, disabled, title }) => {
    return (
        <button
            type='button'
            onClick={onClick}
            disabled={disabled}
            className={cn(
                'flex items-center gap-2 text-sm text-gray-800 transition-opacity',
                className,
                disabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer hover:opacity-80'
            )}
        >
            <span className={rotate}>{icon}</span>
            <span className='text-sm font-semibold text-gray-600'>{title}</span>
        </button>
    );
};

export default ProgramCalendarHead;

